import { FC, useEffect } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useAtomValue, useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import appConfig from 'app-config';
import RecentMatchCounter from 'src/components/AzarOnly/RecentMatchCounter';
import Gap from 'src/components/Gap';
import LoginApple from 'src/components/LoginApple';
import LoginFacebook from 'src/components/LoginFacebook';
import LoginGoogle from 'src/components/LoginGoogle';
import LoginPhone from 'src/components/LoginPhone';
import TextLogoBase from 'src/components/Logo/TextLogo';
import ModalLayoutBase, { ModalHeader, MobileModalType, ModalLoading } from 'src/components/ModalLayout';
import { useGetConsents } from 'src/hooks/useGetConsents';
import useHelpCenterUrl from 'src/hooks/useHelpCenterUrl';
import useLogin from 'src/hooks/useLogin';
import useSelectedTab from 'src/hooks/useSelectedTab';
import { isLoginLoadingAtom } from 'src/stores/auth/atoms';
import { closeModalAtom } from 'src/stores/modal/atoms';
import { AppDownloadLinkType } from 'src/types/AppConfig';
import { EVENT_LOGIN_MODAL_VISITOR_TRIGGER, EVENT_NAME, EVENT_TYPE, EventLoginModalVisitorTrigger } from 'src/types/Event';
import { LoginBrandName } from 'src/types/Login';
import { ModalNavButtonType, ModalType } from 'src/types/Modal';
import { isStringInArray } from 'src/utils/common';
import { isPwaMode } from 'src/utils/device/info';
import { pushEvent } from 'src/utils/event-gateway';
import { setTargetBlankToLinks } from 'src/utils/html';
import LoginButton from './Button';
const LOGIN_BUTTON_MAP = {
  apple: {
    provider: LoginApple,
    brand: LoginBrandName.APPLE
  },
  google: {
    provider: LoginGoogle,
    'data-testid': 'Login-button-google',
    brand: LoginBrandName.GOOGLE
  },
  phone: {
    provider: LoginPhone,
    'data-testid': 'Login-button-phone-number',
    brand: LoginBrandName.PHONE
  },
  facebook: {
    provider: LoginFacebook,
    brand: LoginBrandName.FACEBOOK
  }
} as const;
const ModalLayout = styled(ModalLayoutBase)`
  width: 400px;
`;
const TextLogo = styled(TextLogoBase)`
  margin-bottom: 32px;
`;
const TermsOfUse = styled.span`
  font-size: 11px;
  line-height: 14px;
  font-weight: 400;
  word-break: keep-all;
  color: ${({
  theme
}) => theme.color.content8};
  text-align: center;
  margin-bottom: 24px;
  a {
    color: ${({
  theme
}) => theme.color.content8};
    font-weight: 600;
  }
`;
const LoginModalCounter = styled(RecentMatchCounter)`
  color: ${({
  theme
}) => theme.color.content12};
  font-size: 40px;
  font-weight: 700;
  line-height: 56px;
`;
const LoginModalCounterDesc = styled.p`
  font-size: 15px;
  color: ${({
  theme
}) => theme.color.content12};
  margin-bottom: 40px;
  text-align: center;
  word-break: break-word;
`;
const Container = styled.div`
  flex-direction: column;
  display: flex;
  padding: 0 40px 40px;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  ${({
  theme
}) => theme.screenSize.mobile} {
    width: 100%;
    height: calc(100% - 58px);
    padding: 0px 24px 16px 24px;
  }
`;
const TopContainer = styled.div`
  flex-direction: column;
  display: flex;
  overflow: hidden;
  justify-content: center;
  align-items: center;
  width: 100%;
  flex-grow: 1;
  ${({
  theme
}) => theme.screenSize.mobile} {
    padding: 0 16px;
  }
`;
const Contact = styled.a`
  margin-top: 32px;
  margin-bottom: 12px;
  color: ${({
  theme
}) => theme.color.contentDeep};
  font-size: 14px;
`;
const DownloadContainer = styled.div`
  display: none;
  ${({
  theme
}) => theme.screenSize.mobile} {
    display: flex;
    flex-direction: column;
    color: ${({
  theme
}) => theme.color.content8};
    font-size: 12px;
    font-weight: 700;
    width: 100%;
    align-items: center;
  }
`;
const DownloadButtonContainer = styled.div`
  margin-top: 12px;
  display: flex;
  height: 29px;
`;
const DownloadButton = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 91px;
  height: 100%;
  background-color: ${({
  theme
}) => theme.color.surface4};
  border-radius: 100px;
  :active {
    background-color: ${({
  theme
}) => theme.color.surface5};
  }
`;
const DownloadIcon = ({
  theme
}: {
  theme: Theme;
}) => css`
  fill: ${theme.color.content12};
  width: 16px;
  height: 16px;
`;
const IcApple = styled(dynamic(() => import('src/assets/images/imgBrandApple.svg')))`
  ${DownloadIcon}
`;
const IcGooglePlay = styled(dynamic(() => import('src/assets/images/imgBrandGooglePlay.svg')))`
  ${DownloadIcon}
`;
export interface Props {
  loginSuccessCallback?: () => void;
  trigger?: EventLoginModalVisitorTrigger;
  landFrom?: string;
}
const AzarLoginEntry: FC<Props> = ({
  loginSuccessCallback,
  trigger,
  landFrom
}) => {
  const {
    t,
    i18n: {
      language
    }
  } = useTranslation();
  const {
    isLogin
  } = useLogin();
  const helpCenterURL = useHelpCenterUrl();
  const {
    selectedTab
  } = useSelectedTab();
  const isLoginLoading = useAtomValue(isLoginLoadingAtom);
  const closeModal = useSetAtom(closeModalAtom);
  const {
    isLoading: isConsentsLoading,
    data: consentsData
  } = useGetConsents();
  const {
    consentsRequired,
    uiType: consentsUiType
  } = consentsData?.data?.result || {};
  const termsOfUse = consentsRequired?.find(({
    type
  }) => type === 'TERM_OF_USE');
  useEffect(() => {
    if (isLogin) {
      if (loginSuccessCallback) {
        loginSuccessCallback();
      }
      closeModal(ModalType.LOGIN);
    }
  }, [isLogin, loginSuccessCallback, closeModal]);
  useEffect(() => {
    if (language && trigger) {
      const eventParams = {
        language,
        trigger,
        ...(isStringInArray(trigger, [EVENT_LOGIN_MODAL_VISITOR_TRIGGER.SEO_AZAR_LOGO, EVENT_LOGIN_MODAL_VISITOR_TRIGGER.SEO_START_VIDEO_CHAT]) && {
          land_from: landFrom
        })
      };
      pushEvent({
        eventType: EVENT_TYPE.SIGN_UP,
        eventName: EVENT_NAME.SCREEN_OPEN__LOGIN,
        eventParams
      });
    }
  }, [language, trigger, landFrom]);
  const handleClickAppLink = (type: AppDownloadLinkType) => {
    pushEvent({
      eventType: EVENT_TYPE.WEB2APP,
      eventName: EVENT_NAME.ANYWHERE__CLICK_TO_APP_BTN,
      eventParams: {
        action_category: 'click',
        tab: selectedTab.name,
        page: 'main',
        target: 'to_app_btn',
        button_type: type,
        referrer: 'loginmodal'
      }
    });
  };
  return <ModalLayout isDim data-testid='login-modal' mobileType={MobileModalType.SCREEN_MODAL} closeWhenClickOutside={false} closeWithESC={false} navButtonType={ModalNavButtonType.CLOSE} data-sentry-element="ModalLayout" data-sentry-component="AzarLoginEntry" data-sentry-source-file="LoginEntry.tsx">
      <ModalHeader data-sentry-element="ModalHeader" data-sentry-source-file="LoginEntry.tsx" />
      {(isLoginLoading || isConsentsLoading) && <ModalLoading type='absolute' />}
      <Container data-sentry-element="Container" data-sentry-source-file="LoginEntry.tsx">
        <TopContainer data-sentry-element="TopContainer" data-sentry-source-file="LoginEntry.tsx">
          <TextLogo width={137} height={36} arWidth={74} arHeight={56} ptWidth={175} ptHeight={36} data-sentry-element="TextLogo" data-sentry-source-file="LoginEntry.tsx" />
          <LoginModalCounter data-sentry-element="LoginModalCounter" data-sentry-source-file="LoginEntry.tsx" />
          <LoginModalCounterDesc data-sentry-element="LoginModalCounterDesc" data-sentry-source-file="LoginEntry.tsx">{t('online__matching_now')}</LoginModalCounterDesc>
          {consentsUiType === 'CONSENT_B' && termsOfUse && <TermsOfUse data-testid='login-modal-termsOfUse' dangerouslySetInnerHTML={{
          __html: setTargetBlankToLinks(termsOfUse.agreementMessage + ' ' + termsOfUse.description)
        }} />}
          {appConfig.availableLoginMethods.map(loginMethod => {
          const buttonProps = LOGIN_BUTTON_MAP[loginMethod];
          if (buttonProps == null) return null;
          const {
            provider: Provider,
            ...restProps
          } = buttonProps;
          return <Provider key={loginMethod} render={({
            onClick
          }) => <LoginButton {...restProps} onClick={onClick} />} />;
        })}
          <Contact href={helpCenterURL} data-sentry-element="Contact" data-sentry-source-file="LoginEntry.tsx">{t('LOGIN__CONTACT_US')}</Contact>
        </TopContainer>
        {!isPwaMode() && <DownloadContainer>
            {t('menu_download_app')}
            <DownloadButtonContainer>
              <DownloadButton href='https://azarlive.onelink.me/fHTc/3555l2eu' target='_blank' rel='noopener noreferrer' onClick={() => handleClickAppLink('appstore')}>
                <IcApple />
              </DownloadButton>
              <Gap width={8} />
              <DownloadButton href='https://azarlive.onelink.me/fHTc/3555l2eu' target='_blank' rel='noopener noreferrer' onClick={() => handleClickAppLink('playstore')}>
                <IcGooglePlay />
              </DownloadButton>
            </DownloadButtonContainer>
          </DownloadContainer>}
      </Container>
    </ModalLayout>;
};
export default AzarLoginEntry;